import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store/root-reducer';

import { buildKpis, type Kpi } from '../../_shared/domain/kpis.domain';

const selectSessions = (state: RootState) => state.sessions;

// ── Sessions table ──
export const selectRows = (state: RootState) => selectSessions(state).rows;
export const selectLoading = (state: RootState) => selectSessions(state).loading;
export const selectError = (state: RootState) => selectSessions(state).error;

// ── KPI stats ──
export const selectStats = (state: RootState) => selectSessions(state).stats;

/** Derived KPI cards; null while stats load (the KPI row renders skeletons). */
export const selectKpis = createSelector(
  selectStats,
  (stats): Kpi[] | null => (stats ? buildKpis(stats.totals) : null),
);

// ── New Session panel: form ──
const selectPanel = (state: RootState) => selectSessions(state).panel;
export const selectPanelOpen = (state: RootState) => selectPanel(state).open;
export const selectPanelType = (state: RootState) => selectPanel(state).type;
export const selectPanelLicenceId = (state: RootState) => selectPanel(state).licenceId;
export const selectPanelListId = (state: RootState) => selectPanel(state).listId;
export const selectPanelCurrentOnly = (state: RootState) => selectPanel(state).currentOnly;
export const selectPanelFilterTypes = (state: RootState) => selectPanel(state).filterTypes;
export const selectPanelIncludedBuckets = (state: RootState) => selectPanel(state).includedBuckets;
export const selectPanelReaskedFrom = (state: RootState) => selectPanel(state).reaskedFrom;

// ── New Session panel: fetched data ──
export const selectLicences = (state: RootState) => selectPanel(state).licences;
export const selectProfileTypes = (state: RootState) => selectPanel(state).profileTypes;
export const selectLists = (state: RootState) => selectPanel(state).lists;
export const selectCandidates = (state: RootState) => selectPanel(state).candidates;
export const selectCompanies = (state: RootState) => selectPanel(state).companies;
export const selectLicencesLoading = (state: RootState) => selectPanel(state).licencesLoading;
export const selectProfileTypesLoading = (state: RootState) =>
  selectPanel(state).profileTypesLoading;
export const selectListsLoading = (state: RootState) => selectPanel(state).listsLoading;
export const selectEntitiesLoading = (state: RootState) => selectPanel(state).entitiesLoading;

/** Lists sorted for the grouped dropdown (invyo/admin origin first). Memoised. */
export const selectSortedLists = createSelector(selectLists, (lists) =>
  [...lists].sort((a, b) => (a.origin === b.origin ? 0 : a.origin === 'invyo' ? -1 : 1)),
);

// ── Create-session write ──
export const selectCreateStatus = (state: RootState) => selectSessions(state).createStatus;
export const selectCreateError = (state: RootState) => selectSessions(state).createError;
export const selectCreatedSessionId = (state: RootState) => selectSessions(state).createdSessionId;
